/**
 * Per-tab record of the client tool uses an executor has taken responsibility
 * for.
 *
 * More than one copy of a conversation can be mounted at once — the chat
 * sidebar and the thread detail page both render `<AgentThread />` — and each
 * derives "what is pending" from its own fetch of the thread record and
 * dedupes only against its own history. Without a shared record both run the
 * same `tool_use_id`: two panels opened, two results submitted for one id. The
 * window is wide, not a tick: `client_visualize_open_panel` navigates from
 * inside its own execution and takes seconds to return, and the copy that
 * mounts on that navigation sees the tool still pending.
 *
 * Claims are never released. Client tools navigate, publish workspace
 * mutations, and create resources; none of it is idempotent, so an id that has
 * run once must not become claimable again just because its owner unmounted. A
 * batch that has started still finishes: the runner is a plain object that
 * outlives the React tree it was created in.
 *
 * Nothing coordinates across tabs, deliberately — a second tab is a second
 * browser surface, and a tool that opens a panel there is doing what the user
 * asked for in that tab.
 */

/** `${threadId}:${toolUseId}` of every tool use an executor has taken. */
const claims = new Set<string>();

function claimKey(threadId: string, toolUseId: string): string {
  return `${threadId}:${toolUseId}`;
}

/**
 * Take responsibility for whichever of `toolUseIds` is still unspoken for.
 *
 * @returns The ids the caller should execute: those that were unclaimed, now
 *   recorded. Ids already claimed are excluded — including ones the caller
 *   itself took on an earlier pass, since re-running is what this prevents. An
 *   empty array means another copy owns this batch; stand down.
 */
export function claimToolUses(
  threadId: string,
  toolUseIds: string[],
): string[] {
  const granted: string[] = [];

  for (const toolUseId of toolUseIds) {
    const key = claimKey(threadId, toolUseId);
    if (claims.has(key)) {
      continue;
    }
    claims.add(key);
    granted.push(toolUseId);
  }

  return granted;
}

/**
 * Drop every claim. Tests only — the registry is module state, so without this
 * one test's claims decide another's outcome.
 */
export function __resetToolUseClaimsForTests(): void {
  claims.clear();
}
